import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Typography, Paper, CircularProgress, 
  Dialog, DialogActions, DialogContent, DialogTitle, 
  TextField, IconButton, Snackbar, Alert, FormControl,
  InputLabel, Select, MenuItem, Chip, Stack
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PublishIcon from '@mui/icons-material/Publish';
import UnpublishedIcon from '@mui/icons-material/Unpublished';
import ImageIcon from '@mui/icons-material/Image';
import { collectionsAPI, booksAPI } from '../services/api';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import GenerateCollectionsDialog from '../components/collections/GenerateCollectionsDialog';

const CollectionSchema = Yup.object().shape({
  title: Yup.string().required('Title is required'),
  description: Yup.string(),
  coverImage: Yup.string().url('Must be a valid URL'),
  books: Yup.array().of(Yup.string()).min(1, 'At least one book is required'),
  status: Yup.string().required('Status is required')
});

export default function CollectionsManagement() {
  const navigate = useNavigate();
  const [collections, setCollections] = useState([]);
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCollection, setEditingCollection] = useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [collectionToDelete, setCollectionToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);

  const fetchCollections = async () => {
    try {
      setLoading(true);
      const response = await collectionsAPI.getAllCollections();
      setCollections(response.data?.collections || []);
    } catch (error) {
      console.error('Error fetching collections:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load collections',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchBooks = async () => {
    try {
      const response = await booksAPI.getAllBooks();
      setBooks(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching books:', error);
    }
  };

  useEffect(() => {
    fetchCollections();
    fetchBooks();
  }, []);

  const handleOpenDialog = (collection = null) => {
    setEditingCollection(collection);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingCollection(null);
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      if (editingCollection) {
        await collectionsAPI.updateCollection(editingCollection.id, values);
        setSnackbar({
          open: true,
          message: 'Collection updated successfully',
          severity: 'success',
        });
      } else {
        await collectionsAPI.createCollection(values);
        setSnackbar({
          open: true,
          message: 'Collection created successfully',
          severity: 'success',
        });
      }
      handleCloseDialog();
      fetchCollections();
      resetForm();
    } catch (error) {
      console.error('Error saving collection:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Error saving collection',
        severity: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (collection) => {
    setCollectionToDelete(collection);
    setConfirmDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await collectionsAPI.deleteCollection(collectionToDelete.id);
      setSnackbar({
        open: true,
        message: 'Collection deleted successfully',
        severity: 'success',
      });
      fetchCollections();
    } catch (error) {
      console.error('Error deleting collection:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Error deleting collection',
        severity: 'error',
      });
    } finally {
      setConfirmDeleteOpen(false);
      setCollectionToDelete(null);
    }
  };

  const handleCollectionClick = (collection) => {
    navigate(`/collections/${collection.id}`);
  };

  const handleGenerateCollections = () => {
    setShowGenerateDialog(true);
  };

  const handleGenerateDialogClose = () => {
    setShowGenerateDialog(false);
  };

  const handleCollectionsSaved = (collections, results) => {
    // Show success message
    setSnackbar({
      open: true,
      message: `${collections.length} collections created successfully`,
      severity: 'success',
    });
    
    // Refresh collections list
    fetchCollections();
  };

  const handleTogglePublishStatus = async (collection) => {
    try {
      const newStatus = collection.status === 'published' ? 'draft' : 'published';
      await collectionsAPI.updateCollection(collection.id, { status: newStatus });
      
      // Update local state
      setCollections(collections.map(c => 
        c.id === collection.id 
          ? { ...c, status: newStatus } 
          : c
      ));
      
      setSnackbar({
        open: true,
        message: `Collection ${newStatus === 'published' ? 'published' : 'unpublished'} successfully`,
        severity: 'success',
      });
    } catch (error) {
      console.error('Error toggling collection status:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update collection status',
        severity: 'error',
      });
    }
  };

  const handleGenerateCover = async (collection) => {
    try {
      // Default prompt based on collection title and description
      const defaultPrompt = `Book collection titled "${collection.title}" containing various books`;
      
      await collectionsAPI.generateCollectionCover(collection.id, {
        prompt: defaultPrompt
      });
      
      setSnackbar({
        open: true,
        message: 'Cover generation started. This may take a minute...',
        severity: 'info',
      });
      
      // Refresh collections after a delay to allow for cover generation
      setTimeout(() => {
        fetchCollections();
      }, 5000);
      
    } catch (error) {
      console.error('Error generating cover:', error);
      setSnackbar({
        open: true,
        message: 'Failed to generate cover',
        severity: 'error',
      });
    }
  };

  const columns = [
    { 
      field: 'title', 
      headerName: 'Title', 
      width: 200,
      renderCell: (params) => (
        <Box sx={{ cursor: 'pointer', textDecoration: 'underline', color: 'primary.main' }} 
             onClick={() => handleCollectionClick(params.row)}>
          {params.value}
        </Box>
      )
    },
    { field: 'description', headerName: 'Description', width: 300 },
    { 
      field: 'status', 
      headerName: 'Status', 
      width: 120,
      renderCell: (params) => (
        <Chip 
          label={params.value} 
          color={params.value === 'published' ? 'success' : 'default'}
          size="small"
        />
      )
    },
    { field: 'bookCount', headerName: 'Books', width: 100, valueGetter: (params) => params.row.books?.length || 0 },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 250, // Increased width to accommodate more buttons
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            size="small"
            color={params.row.status === 'published' ? 'warning' : 'success'}
            startIcon={params.row.status === 'published' ? <UnpublishedIcon /> : <PublishIcon />}
            onClick={(e) => {
              e.stopPropagation();
              handleTogglePublishStatus(params.row);
            }}
          >
            {params.row.status === 'published' ? 'Unpublish' : 'Publish'}
          </Button>
          <IconButton 
            color="primary"
            onClick={(e) => {
              e.stopPropagation();
              handleGenerateCover(params.row);
            }}
            title="Generate Cover"
          >
            <ImageIcon />
          </IconButton>
          <IconButton 
            onClick={(e) => {
              e.stopPropagation();
              handleOpenDialog(params.row);
            }}
            title="Edit"
          >
            <EditIcon />
          </IconButton>
          <IconButton 
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteClick(params.row);
            }}
            title="Delete"
          >
            <DeleteIcon />
          </IconButton>
        </Stack>
      )
    }
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Collections Management</Typography>
        <Stack direction="row" spacing={2}>
          {/* Generate Collections button */}
          <Button 
            variant="outlined"
            color="secondary"
            startIcon={<AutoAwesomeIcon />}
            onClick={handleGenerateCollections}
          >
            Generate Collections
          </Button>
          
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Collection
          </Button>
        </Stack>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ height: 500, width: '100%' }}>
          <DataGrid
            rows={collections}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            onRowClick={(params) => handleCollectionClick(params.row)}
          />
        </Paper>
      )}

      {/* Create/Edit Collection Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingCollection ? 'Edit Collection' : 'Add New Collection'}
        </DialogTitle>
        <Formik
          initialValues={{
            title: editingCollection?.title || '',
            description: editingCollection?.description || '',
            coverImage: editingCollection?.coverImage || '',
            books: editingCollection?.books || [],
            status: editingCollection?.status || 'draft'
          }}
          validationSchema={CollectionSchema}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, handleChange, setFieldValue, isSubmitting }) => (
            <Form>
              <DialogContent>
                <TextField
                  fullWidth
                  margin="dense"
                  id="title"
                  name="title"
                  label="Collection Title"
                  value={values.title}
                  onChange={handleChange}
                  error={touched.title && Boolean(errors.title)}
                  helperText={touched.title && errors.title}
                />
                
                <TextField
                  fullWidth
                  margin="dense"
                  id="description"
                  name="description"
                  label="Description"
                  multiline
                  rows={3}
                  value={values.description}
                  onChange={handleChange}
                  error={touched.description && Boolean(errors.description)}
                  helperText={touched.description && errors.description}
                />
                
                <TextField
                  fullWidth
                  margin="dense"
                  id="coverImage"
                  name="coverImage"
                  label="Cover Image URL"
                  value={values.coverImage}
                  onChange={handleChange}
                  error={touched.coverImage && Boolean(errors.coverImage)}
                  helperText={touched.coverImage && errors.coverImage}
                />
                
                <FormControl fullWidth margin="dense" error={touched.books && Boolean(errors.books)}>
                  <InputLabel id="books-select-label">Books</InputLabel>
                  <Select
                    labelId="books-select-label"
                    id="books"
                    name="books"
                    multiple
                    value={values.books}
                    onChange={handleChange}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((bookId) => {
                          const book = books.find(b => b.id === bookId);
                          return (
                            <Chip key={bookId} label={book ? book.title : bookId} />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {books.map((book) => (
                      <MenuItem key={book.id} value={book.id}>
                        {book.title}
                      </MenuItem>
                    ))}
                  </Select>
                  {touched.books && errors.books && (
                    <Typography color="error" variant="caption" sx={{ ml: 2 }}>
                      {errors.books}
                    </Typography>
                  )}
                </FormControl>
                
                <FormControl fullWidth margin="dense">
                  <InputLabel id="status-select-label">Status</InputLabel>
                  <Select
                    labelId="status-select-label"
                    id="status"
                    name="status"
                    value={values.status}
                    onChange={handleChange}
                  >
                    <MenuItem value="draft">Draft</MenuItem>
                    <MenuItem value="processing">Processing</MenuItem>
                    <MenuItem value="published">Published</MenuItem>
                  </Select>
                </FormControl>
              </DialogContent>
              
              <DialogActions>
                <Button onClick={handleCloseDialog}>Cancel</Button>
                <Button 
                  type="submit" 
                  variant="contained" 
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Saving...' : 'Save'}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the collection "{collectionToDelete?.title}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleConfirmDelete} 
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Use the new GenerateCollectionsDialog component */}
      <GenerateCollectionsDialog
        open={showGenerateDialog}
        onClose={handleGenerateDialogClose}
        onSave={handleCollectionsSaved}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
